<template>
    <v-card flat tile>
        <v-row no-gutters class="d-flex"
            ><filter-menu
                v-model="filterMenuOpen"
                :activeField="filterMenuField"
                class="mb-1 ml-auto mr-2"
                :fields="filterFields"
                :initialFilters="filters"
                @filtersUpdated="filtersUpdated"
                :dense="true"></filter-menu
        ></v-row>
        <v-data-table
            id="mentionTable"
            :headers="headers"
            :items="promptResponses"
            :loading="loading"
            :server-items-length="total"
            :options.sync="options"
            :footer-props="footerProps"
            @click:row="onRowInteraction"
            height="70vh"
            fixed-header
            :items-per-page.sync="itemsPerPage"
            @update:options="onOptionsUpdate"
            class="elevation-0">
            <template v-for="(header_entry, index) in headers" v-slot:[`header.${header_entry.value}`]="{ header }">
                <span :key="index">
                    <span v-bind:style="{ width: header.width }">
                        <v-btn icon x-small class="mr-1 mb-1" color="primary" style="cursor: pointer" @click.stop="openFilterMenu(header.value)">
                            <v-icon v-if="header.filterable" small>mdi-filter-variant</v-icon></v-btn
                        >
                        {{ header.text }}
                    </span>
                </span>
            </template>
            <template v-slot:item.importDate="{ item }">
                {{ formatDate(item.importDate) }}
            </template>
            <template v-slot:item.prompt="{ item }">
                <span style="white-space: pre-line">{{ item.prompt }}</span>
            </template>
            <template v-slot:item.mention="{ item }">
                <span style="max-width: 500px" v-html="renderMentionMarkdown(item)"></span>
            </template>
            <template v-slot:item.brands="{ item }">
                <div class="entity-chip-list">
                    <v-tooltip bottom v-for="(entry, idx) in getDisplayEntities(item).slice(0, 4)" :key="entry.key || idx">
                        <template v-slot:activator="{ on, attrs }">
                            <v-chip small class="entity-chip ma-1" outlined v-bind="attrs" v-on="on" :style="entityChipStyle(entry.entity)">
                                <span class="entity-chip-dot" :style="entityDotStyle(entry.entity)"></span>
                                <span class="entity-chip-label" :class="{ 'font-weight-bold': isProjectBrandEntity(entry) }">{{ entry.label }}</span>
                            </v-chip>
                        </template>
                        <span>{{ entry.label }}</span>
                    </v-tooltip>

                    <v-tooltip bottom v-if="getDisplayEntities(item).length > 4">
                        <template v-slot:activator="{ on, attrs }">
                            <v-chip small outlined class="entity-chip-more ma-1" color="grey darken-1" text-color="grey darken-1" v-bind="attrs" v-on="on">
                                +{{ getDisplayEntities(item).length - 4 }}
                            </v-chip>
                        </template>
                        <div class="entity-more-tooltip">
                            <div class="entity-more-tooltip-row" v-for="(entry, i) in getHiddenEntities(item)" :key="entry.key || i">
                                <span :class="{ 'entity-more-tooltip-label--primary': isProjectBrandEntity(entry) }">{{ entry.label }}</span>
                            </div>
                        </div>
                    </v-tooltip>
                </div>
            </template>
            <template v-slot:item.sentiment="{ item }">
                <div class="mention-sentiment-chip">
                    <span class="senitmentchip" :class="getSentimentChipClass(item)">{{ getSentimentForBrand(item) }}</span>
                </div>
            </template>
            <template v-slot:item.sources="{ item }">
                <div class="d-flex flex-wrap" style="max-width: 300px; align-items: center">
                    <v-tooltip bottom v-for="(src, i) in sortedSources(item.sources)" :key="src._key || i">
                        <template v-slot:activator="{ on, attrs }">
                            <v-chip
                                v-bind="attrs"
                                v-on="on"
                                small
                                outlined
                                class="ma-1"
                                color="grey darken-1"
                                text-color="grey darken-1"
                                @click.stop
                                :href="src.url || null"
                                target="_blank"
                                rel="noopener">
                                {{ src.domain || "—" }}
                            </v-chip>
                        </template>
                        <span>{{ src.url || src.key || src.domain }}</span>
                    </v-tooltip>
                    <span v-if="extraSourceCount(item.sources) > 0" class="grey--text caption ma-1">+{{ extraSourceCount(item.sources) }}</span>
                </div>
            </template>
            <template v-slot:[`item.platform`]="{ item }">
                <span class="platform-icon-wrapper" v-if="getPlatformInfo(item.platform).logo" style="display: flex; align-items: center">
                    <span class="platform-icon" v-html="getPlatformInfo(item.platform).logo" style="margin: auto"></span>
                    <!-- {{ getPlatformInfo(item.platform).label }} -->
                </span>
                <span v-else>
                    {{ item.platform }}
                </span>
            </template>
        </v-data-table>
        <v-progress-linear v-if="loading || !initialFiltersReady" indeterminate height="2"></v-progress-linear>
        <chat-dialog :value="dialog" :item="selectedItem" @input="dialog = $event" :selectedPlatform="selectedPlatform" />
    </v-card>
</template>

<script>
    import FilterMenu from "../monitoring/FilterMenu.vue"
    import { AMAZON_RUFUS_LOGO } from "../../utils/platformMeta"

    import MarkdownIt from "markdown-it"
    import { getBrandColorFromName } from "../../utils/brandColor"
    export default {
        inject: {
            // Von AIProject bereitgestellt: öffnet das Prompt-Detail als Overlay statt per Route
            aiSuiteOpenPromptDetail: { default: null }
        },
        props: {
            brand: { type: Object, required: true },
            brands: { type: Array, required: true },
            isActive: { type: Boolean, default: false },
            search: { type: String },
            fromDate: { type: String, required: true },
            toDate: { type: String, required: true },
            projectId: { type: String, required: true },
            platforms: { type: Array, required: true },
            selectedPlatform: Object,
            project: Object,
            sentimentAvailable: { type: Boolean, default: false },
            tags: Array,
            tagOperator: String,
            selectedDates: Array,
            initialFilters: {
                type: Array,
                default: null
            },
            initialFiltersReady: {
                type: Boolean,
                default: true
            }
        },
        components: {
            FilterMenu
        },
        data() {
            return {
                filterMenuOpen: false,
                filterMenuField: null,
                filters: [],
                md: new MarkdownIt(),
                promptResponses: [],
                options: {
                    page: 1,
                    itemsPerPage: 25,
                    sortBy: [],
                    sortDesc: []
                },
                itemsPerPage: 25,
                footerProps: {
                    itemsPerPageOptions: [10, 25, 50, 100],
                    showFirstLastPage: true
                },
                skip: 0,
                total: 0,
                loading: false
            }
        },
        watch: {
            initialFilters: {
                immediate: true,
                handler(newVal) {
                    if (!Array.isArray(newVal)) return
                    this.filters = newVal
                }
            },
            initialFiltersReady(newVal) {
                if (newVal && this.isActive) this.fetchData()
            },
            search() {
                if (!this.isActive) return
                clearTimeout(this._searchDebounce)
                this._searchDebounce = setTimeout(() => {
                    this.options.page = 1
                    this.fetchData()
                }, 400)
            },
            brand() {
                this.options.page = 1
                if (this.isActive) this.fetchData()
            },
            fromDate() {
                this.options.page = 1
                if (this.isActive) this.fetchData()
            },
            toDate() {
                this.options.page = 1
                if (this.isActive) this.fetchData()
            },
            selectedPlatform() {
                this.options.page = 1
                if (this.isActive) this.fetchData()
            },
            tags() {
                this.options.page = 1
                if (this.isActive) this.fetchData()
            },
            isActive(newVal) {
                if (newVal) {
                    this.options.page = 1
                    this.fetchData()
                }
            }
        },
        computed: {
            headers() {
                let h = [
                    { text: "Platform", value: "platform", align: "start", filterable: false },
                    { text: "Prompt", value: "prompt", align: "start", filterable: true, width: "350px" },
                    { text: "Mention", value: "mention", align: "start", filterable: true, width: "400px" },
                    { text: "Brands", value: "brands", class: "col-brands", cellClass: "col-brands", width: "300px", minWidth: "320px", align: "start", filterable: true },
                    // { text: "Sentiment (%)", value: "sentiment", align: "start", filterable: true },
                    { text: "Sources", value: "sources", align: "start", filterable: true },
                    { text: "Updated", value: "importDate", align: "start", filterable: false }
                ]
                if (this.sentimentAvailable) {
                    h.splice(3, 0, { text: `Sentiment ${this.project?.brand?.brand_name || ""}`, value: "sentiment", align: "start", filterable: true })
                }
                return h
            },
            isAllPlatforms() {
                // NEU
                return this.selectedPlatform && this.selectedPlatform.key === "all"
            },
            filterFields() {
                return [
                    { name: "Mention", key: "mention", type: "text", filterable: true },
                    { name: "Prompt", key: "prompt", type: "text", filterable: true },
                    {
                        name: "Brand",
                        key: "brands",
                        type: "array",
                        options: {
                            values: this.brands ? this.brands.map((b) => b.canonicalName) : []
                        },
                        multiple: true,
                        filterable: true
                    },
                    { name: "Sentiment (%)", key: "sentiment", type: "number", filterable: true },
                    { name: "Sources", key: "sources", type: "text", filterable: true }
                ]
            }
        },
        methods: {
            sortedSources(sources) {
                const arr = Array.isArray(sources) ? sources.slice() : []
                // berechne Domain pro Source und baue Key
                const withDomains = arr.map((s, idx) => {
                    const domain = (s.domain && String(s.domain).toLowerCase()) || this.domainFromUrl(s.url || s.key || "")
                    return { ...s, domain, _key: (s.url || s.key || s.domain || "") + "::" + idx }
                })

                const preferDomain =
                    (this.project && this.project.brand && this.project.brand.brand_domain && this.project.brand.brand_domain.toLowerCase()) ||
                    (this.brand && this.brand.brand_domain && this.brand.brand_domain.toLowerCase()) ||
                    ""

                // sortiere: preferDomain zuerst, Rest anschließend; max. 5 anzeigen
                const preferred = preferDomain ? withDomains.filter((s) => s.domain === preferDomain) : []
                const others = withDomains.filter((s) => !preferDomain || s.domain !== preferDomain)
                return preferred.concat(others).slice(0, 5)
            },
            extraSourceCount(sources) {
                const len = Array.isArray(sources) ? sources.length : 0
                return len > 5 ? len - 5 : 0
            },
            openFilterMenu(fieldKey) {
                // Trick: Feld kurz auf null setzen, damit der Watcher wieder feuert
                this.filterMenuField = null
                this.$nextTick(() => {
                    this.filterMenuField = fieldKey
                    this.filterMenuOpen = true
                })
            },
            filtersUpdated(filters) {
                // Brand-Mentions ergänzen wie in PromptsTable
                filters.forEach((f) => {
                    if (f.field && f.field.key === "brands") {
                        const brandObj = this.brands.find((b) => b.canonicalName === f.value)
                        if (brandObj) {
                            f.mentions = brandObj.mentions
                        }
                    }
                })
                this.filters = filters
                this.$emit("filtersUpdated", this.filters)
                this.fetchData()
            },
            getActiveBrandTokens() {
                const tokens = new Set()
                const push = (value) => {
                    if (typeof value === "string" && value.trim()) tokens.add(value.trim().toLowerCase())
                }
                if (this.brand) {
                    push(this.brand.canonicalName)
                    push(this.brand.canonical_name)
                    push(this.brand.brand_name)
                    push(this.brand.brandName)
                    push(this.brand.name)
                }
                if (this.project?.brand?.brand_name) push(this.project.brand.brand_name)
                if (Array.isArray(this.brand?.mentions)) {
                    this.brand.mentions.forEach((mention) => {
                        if (typeof mention === "string") push(mention)
                        else if (mention) {
                            push(mention.text?.content)
                            push(mention.content)
                        }
                    })
                }
                return tokens
            },
            getSentimentEntity(item) {
                if (!item || !Array.isArray(item.mergedEntities)) return null
                const tokens = this.getActiveBrandTokens()
                if (!tokens.size) return null
                return (
                    item.mergedEntities.find((entity) => {
                        const primaryName = (entity.canonicalName || entity.name || "").toLowerCase()
                        if (primaryName && tokens.has(primaryName)) return true
                        if (Array.isArray(entity.mentions)) {
                            return entity.mentions.some((mention) => {
                                const raw = typeof mention === "string" ? mention : mention?.text?.content || mention?.content || ""
                                return raw && tokens.has(raw.toLowerCase())
                            })
                        }
                        return false
                    }) || null
                )
            },
            getSentimentScore(item) {
                const entity = this.getSentimentEntity(item)
                if (!entity || entity.sentiment_score === undefined || entity.sentiment_score === null) return null
                const score = Number(entity.sentiment_score)
                return Number.isFinite(score) ? score : null
            },
            getSentimentForBrand(item) {
                const score = this.getSentimentScore(item)
                if (score === null) return "-"
                return `${(score * 100).toFixed(0)}`
            },
            getSentimentChipClass(item) {
                const score = this.getSentimentScore(item)
                if (score === null) return "senitmentchip--neutral"
                if (score > 0) return "senitmentchip--positive"
                if (score < 0) return "senitmentchip--negative"
                return "senitmentchip--neutral"
            },
            onRowInteraction(item) {
                if (!item) return
                const projectId = this.project?._id || this.projectId || this.$route?.params?.id
                if (!projectId) return
                const query = {}
                if (item.task_id || item.taskId) query.task_id = item.task_id || item.taskId
                if (item.prompt) query.prompt = item.prompt
                if (item.platform) query.platform = item.platform
                const routeBrand = this.getRouteBrandPayload()
                if (routeBrand) query.brand = routeBrand
                const dateRange = Array.isArray(this.selectedDates) ? this.selectedDates.filter(Boolean) : []
                if (dateRange.length) {
                    query.dates = JSON.stringify(dateRange)
                } else if (this.fromDate || this.toDate) {
                    const toDay = (d) => {
                        if (!d) return null
                        const dt = new Date(d)
                        return Number.isNaN(dt.getTime()) ? null : dt.toISOString().slice(0, 10)
                    }
                    const fromDay = toDay(this.fromDate)
                    const toDayVal = toDay(this.toDate) || fromDay
                    const fallbackRange = [fromDay, toDayVal].filter(Boolean)
                    if (fallbackRange.length) query.dates = JSON.stringify(fallbackRange)
                }
                // Bevorzugt als Overlay im AIProject öffnen (kein Routenwechsel, View bleibt erhalten)
                if (this.aiSuiteOpenPromptDetail) {
                    this.aiSuiteOpenPromptDetail({ ...query, project_id: projectId })
                    return
                }
                this.$router.push({
                    name: "PromptDetail",
                    params: { id: projectId },
                    query
                })
            },
            getRouteBrandPayload() {
                const matched = this.findMatchedBrandByProjectName()
                if (!matched) return null

                try {
                    return JSON.stringify({
                        canonicalName: matched.canonicalName || matched.brand_name || matched.brand || matched.name || null,
                        brand_name: matched.brand_name || matched.brand || matched.canonicalName || matched.name || null,
                        brand: matched.brand || matched.brand_name || matched.canonicalName || matched.name || null,
                        mentions: Array.isArray(matched.mentions) ? matched.mentions : []
                    })
                } catch (e) {
                    return null
                }
            },
            findMatchedBrandByProjectName() {
                const projectBrandName = this.project?.brand?.brand_name || this.brand?.brand_name || this.brand?.name || ""
                const projectToken = this.normalizeEntityToken(projectBrandName)
                if (!projectToken || !Array.isArray(this.brands) || !this.brands.length) return null

                const getMentionValue = (mention) => {
                    if (mention == null) return ""
                    if (typeof mention === "string" || typeof mention === "number") return String(mention)
                    return mention?.text?.content || mention?.content || mention?.canonicalName || mention?.name || mention?.brand || mention?.entity || ""
                }

                return (
                    this.brands.find((entry) => {
                        const mentions = Array.isArray(entry?.mentions) ? entry.mentions : []
                        return mentions.some((mention) => this.normalizeEntityToken(getMentionValue(mention)) === projectToken)
                    }) || null
                )
            },
            getBrandWebsiteForEntity(entity) {
                if (!entity) return null
                if (!Array.isArray(this.brands)) return null

                const toLower = (s) => (typeof s === "string" ? s.toLowerCase() : "")

                const extractDomainsFromString = (s) => {
                    if (!s || typeof s !== "string") return []
                    const cleaned = s.replace(/^https?:\/\//i, "").replace(/^www\./i, "")
                    const m = cleaned.match(/[a-z0-9-]+(?:\.[a-z0-9-]+)+/i)
                    return m ? [m[0].toLowerCase()] : []
                }
                const extractDomainsFromList = (list) => {
                    const out = []
                    ;(list || []).forEach((m) => {
                        if (typeof m === "string") out.push(...extractDomainsFromString(m))
                        else if (m) {
                            if (m.content) out.push(...extractDomainsFromString(m.content))
                            if (m.text?.content) out.push(...extractDomainsFromString(m.text.content))
                        }
                    })
                    return out
                }

                const entityNames = [entity.canonicalName, entity.name].filter(Boolean).map(toLower)

                // Brand-Match robuster
                const match = (this.brands || []).find((b) => {
                    const pool = []
                    if (b?.canonicalName) pool.push(b.canonicalName)
                    if (Array.isArray(b?.mentions)) {
                        b.mentions.forEach((m) => {
                            if (typeof m === "string") pool.push(m)
                            else if (m) {
                                if (m.content) pool.push(m.content)
                                if (m.text?.content) pool.push(m.text.content)
                            }
                        })
                    }
                    return pool.some((p) => p && entityNames.includes(p.toLowerCase()))
                })

                // Kandidaten sammeln
                const candidates = new Set()

                // 0) Domain direkt aus Entity-Name (z. B. "Douglas.de")
                entityNames.forEach((n) => extractDomainsFromString(n).forEach((d) => candidates.add(d)))

                // 1) Metadaten
                if (match?.metadata?.officialWebsite) {
                    extractDomainsFromString(match.metadata.officialWebsite).forEach((d) => candidates.add(d))
                }

                // 2) Mentions (Brand + Entity)
                extractDomainsFromList(match?.mentions).forEach((d) => candidates.add(d))
                extractDomainsFromList(entity.mentions).forEach((d) => candidates.add(d))

                const list = Array.from(candidates)
                if (!list.length) {
                    // Konservativer Fallback (Whitelist, niemals Roh-Sprachcode)
                    const lang = toLower(this.project?.langloc?.language_code || "")
                    const country = toLower(this.project?.langloc?.country_code || "")
                    const tldByCountry = {
                        de: "de",
                        at: "at",
                        ch: "ch",
                        gb: "uk",
                        uk: "uk",
                        us: "com",
                        it: "it",
                        hu: "hu",
                        cz: "cz",
                        hr: "hr",
                        si: "si",
                        sk: "sk",
                        rs: "rs",
                        ro: "ro",
                        bg: "bg",
                        mk: "mk",
                        es: "es",
                        fr: "fr",
                        nl: "nl"
                    }
                    const tldByLang = { en: "com", de: "de", it: "it", es: "es", fr: "fr", nl: "nl" }
                    const tld = tldByCountry[country] || tldByLang[lang] || "com"

                    const raw = toLower(entity.canonicalName || entity.name || "").replace(/\.[a-z]{2,}$/, "")
                    const cleaned = raw
                        .replace(/\b(gmbh|ag|inc|ltd|co|corp|company|sarl|sa|kg|mbh)\b/g, "")
                        .replace(/&/g, " und ")
                        .replace(/[^a-z0-9]+/g, "-")
                        .replace(/-+/g, "-")
                        .replace(/^-|-$/g, "")
                    if (cleaned && cleaned.length >= 2) return `${cleaned}.${tld}`
                    return null
                }

                // Bevorzugung: erst Country, dann Language, dann gängige TLDs
                const prefer = []
                const cc = toLower(this.project?.langloc?.country_code || "")
                if (cc) prefer.push(cc)
                const lc = toLower(this.project?.langloc?.language_code || "")
                if (lc && lc !== cc) prefer.push(lc)
                prefer.push("com", "de", "net", "org")

                for (const t of prefer) {
                    const hit = list.find((d) => d.endsWith(`.${t}`))
                    if (hit) return hit
                }
                return list[0]
            },
            onOptionsUpdate(options) {
                this.options = options
                this.itemsPerPage = options.itemsPerPage
                this.skip = (options.page - 1) * options.itemsPerPage
                this.fetchData()
            },
            buildAllPlatformsDateRange(limitPerPlatform = 2, selectedDays = null) {
                if (!this.project) return null
                const fields = {
                    openai: "processed_dates_ai_tracking_openai",
                    chatgpt: "processed_dates_ai_tracking_chatgpt",
                    google: "processed_dates",
                    google_ai_mode: "processed_dates_ai_tracking_google_ai_mode",
                    gemini: "processed_dates_ai_tracking_gemini",
                    claude: "processed_dates_ai_tracking_claude",
                    perplexity: "processed_dates_ai_tracking_perplexity",
                    grok: "processed_dates_ai_tracking_grok",
                    microsoft_copilot: "processed_dates_ai_tracking_microsoft_copilot",
                    amazon_rufus: "processed_dates_ai_tracking_amazon_rufus"
                }

                const toUtcDay = (d) => {
                    const dt = new Date(d)
                    return isNaN(dt) ? null : dt.toISOString().slice(0, 10)
                }

                // NEU: wenn selectedDays übergeben -> nutze Range vom frühesten TS des ersten Tages
                // bis zum spätesten TS des letzten Tages (plattformübergreifend)
                if (Array.isArray(selectedDays) && selectedDays.length) {
                    const days = selectedDays.map(toUtcDay).filter(Boolean).sort()
                    if (!days.length) return null

                    const firstDay = days[0]
                    const lastDay = days[days.length - 1]

                    let earliest = null
                    let latest = null

                    Object.values(fields).forEach((f) => {
                        const arr = this.project[f]
                        if (!Array.isArray(arr) || !arr.length) return
                        arr.forEach((d) => {
                            const dt = new Date(d)
                            if (isNaN(dt)) return
                            const day = dt.toISOString().slice(0, 10)
                            if (day === firstDay) {
                                if (!earliest || dt < earliest) earliest = dt
                            }
                            if (day === lastDay) {
                                if (!latest || dt > latest) latest = dt
                            }
                        })
                    })

                    const from = earliest ? earliest.toISOString() : `${firstDay}T00:00:00.000Z`
                    const to = latest ? latest.toISOString() : `${lastDay}T23:59:59.999Z`
                    if (new Date(from) > new Date(to)) return null
                    return { from, to }
                }

                // ALT: letzter N-Werte pro Plattform → globale Min/Max
                let all = []
                Object.values(fields).forEach((f) => {
                    const arr = this.project[f]
                    if (Array.isArray(arr) && arr.length) {
                        all = all.concat(arr.slice(-limitPerPlatform))
                    }
                })
                if (!all.length) return null
                const parsed = all
                    .map((d) => {
                        const dt = new Date(d)
                        return isNaN(dt) ? null : dt
                    })
                    .filter(Boolean)
                    .sort((a, b) => a - b)
                if (!parsed.length) return null
                return {
                    from: parsed[0].toISOString(),
                    to: parsed[parsed.length - 1].toISOString()
                }
            },
            async fetchData() {
                if (!this.initialFiltersReady) return
                if (!this.brand) return
                this.loading = true
                let sort = {}
                if (this.options.sortBy && this.options.sortBy.length) {
                    sort[this.options.sortBy[0]] = this.options.sortDesc && this.options.sortDesc[0] ? -1 : 1
                } else {
                    sort = { importDate: -1 }
                }

                const url =
                    (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                        this.CryptoJS.AES.decrypt(localStorage.company_id, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    ) == -1
                        ? process.env.VUE_APP_MONITORING_API_URL
                        : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/promptresponses/by-brand"
                let base = {
                    company_id: localStorage.company_id,
                    project_id: this.projectId,
                    brand: this.brand,
                    skip: (this.options.page - 1) * this.itemsPerPage,
                    limit: this.itemsPerPage,
                    sort
                }
                if (this.tags?.length) {
                    base.tags = this.tags
                    base.tagsOperator = this.tagOperator
                }
                if (this.search) base.search = this.search
                if (this.filters?.length) base.filters = this.filters

                if (this.isAllPlatforms) {
                    const range = this.buildAllPlatformsDateRange(2, [this.fromDate, this.toDate])
                    if (range) {
                        base.importDateRange = range
                    } else {
                        // Fallback: nutze aktuelle from/to
                        base.fromDate = this.fromDate
                        base.toDate = this.toDate || this.fromDate
                    }
                } else {
                    base.fromDate = this.fromDate
                    base.toDate = this.toDate || this.fromDate
                    if (this.selectedPlatform?.key) {
                        base.platform = this.selectedPlatform.key
                    }
                }

                try {
                    const res = await this.$helpers.axiosPost(url, base)
                    this.promptResponses = res.data.responses || []
                    this.total = res.data.total || 0
                } catch (e) {
                    console.error("MentionTable fetchData error", e)
                } finally {
                    this.loading = false
                }
            },
            formatDate(date) {
                if (!date) return ""
                return new Date(date).toLocaleDateString()
            },
            getMentionSnippet(item) {
                if (!item.mergedEntities || !Array.isArray(item.mergedEntities)) return "-"

                const sanitizeForMentionMatch = (text) => {
                    if (!text) return ""
                    let out = String(text)
                    out = out.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
                    out = out.replace(/(https?:\/\/[^\s)]+|www\.[^\s)]+)/gi, "")
                    return out
                }

                const isMentionMatch = (entityName, mentionsArr) => {
                    if (!entityName || !Array.isArray(mentionsArr)) return false
                    const lc = entityName.toLowerCase()
                    return mentionsArr.some((m) => {
                        if (typeof m === "string") return m.toLowerCase() === lc
                        if (m && m.text && m.text.content) return m.text.content.toLowerCase() === lc
                        return false
                    })
                }

                const match = item.mergedEntities.find((e) => e.name && isMentionMatch(e.name, this.brand.mentions))
                if (!match) return "-"

                const responseText = item.responseText || item.response_text || item.response || item.answer || item.content || item.fullText || item.text || item.prompt || ""
                if (typeof responseText !== "string" || !responseText.trim().length) return "-"

                // Bilder (z. B. ChatGPT-Shopping-Karussell) gehoeren nicht in den Snippet:
                // verlinkte Bilder [![alt](img)](url) und reine Bilder ![alt](img) komplett entfernen
                const textWithoutImages = responseText.replace(/\[!\[[^\]]*\]\([^)]*\)\]\([^)]*\)/g, "").replace(/!\[[^\]]*\]\([^)]*\)/g, "")

                const rawMentions = Array.isArray(this.brand.mentions) ? this.brand.mentions.slice() : []
                if (match.name && !rawMentions.includes(match.name)) rawMentions.unshift(match.name)
                const normMentions = rawMentions.map((m) => (typeof m === "string" ? m : m?.text?.content || m?.content || "")).filter((m) => m && m.trim().length)
                if (!normMentions.length) return "-"

                const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
                const mentionRe = new RegExp(`(?:^|[^\\p{L}\\p{N}_])(?:${normMentions.map(escapeRe).join("|")})(?=$|[^\\p{L}\\p{N}_])`, "iu")
                const containsMention = (text) => {
                    if (!text) return false
                    return mentionRe.test(sanitizeForMentionMatch(text))
                }

                const lines = textWithoutImages.split("\n")
                const isListLine = (line) => /^\s*(?:[-*+]|\d+\.|\d+\))\s+/.test(line)
                const isTableLine = (line) => /^\s*\|.*\|\s*$/.test(line) || /\s\|\s/.test(line)

                // Finde die ERSTE Zeile, die die Brand enthaelt
                let hitIdx = -1
                for (let i = 0; i < lines.length; i++) {
                    if (containsMention(lines[i])) {
                        hitIdx = i
                        break
                    }
                }
                if (hitIdx === -1) return "-"

                const hitLine = lines[hitIdx]
                const ensure = (s) => (s && containsMention(s) ? s : null)

                // a) Tabellenzeile -> nur diese Zeile
                if (isTableLine(hitLine)) {
                    return ensure(hitLine.trim()) || "-"
                }

                // b) Listeneintrag (Hit-Zeile selbst oder Continuation)
                let listStartIdx = -1
                if (isListLine(hitLine)) {
                    listStartIdx = hitIdx
                } else if (/^\s+/.test(hitLine)) {
                    for (let j = hitIdx - 1; j >= 0; j--) {
                        if (isListLine(lines[j])) {
                            listStartIdx = j
                            break
                        }
                        if (lines[j].trim() === "") break
                    }
                }
                if (listStartIdx !== -1) {
                    const chunk = [lines[listStartIdx]]
                    for (let k = listStartIdx + 1; k < lines.length; k++) {
                        const l = lines[k]
                        if (l.trim() === "") break
                        if (isListLine(l)) break
                        if (!/^\s+/.test(l)) break
                        chunk.push(l)
                    }
                    const listSnippet = ensure(chunk.join("\n").trim())
                    if (listSnippet) return listSnippet
                }

                // c) Satz mit Mention im umgebenden Absatz
                let paraStart = hitIdx
                while (paraStart > 0 && lines[paraStart - 1].trim() !== "") paraStart--
                let paraEnd = hitIdx
                while (paraEnd < lines.length - 1 && lines[paraEnd + 1].trim() !== "") paraEnd++
                const paragraph = lines
                    .slice(paraStart, paraEnd + 1)
                    .join(" ")
                    .trim()
                const sentences = paragraph.match(/[^.!?\n]+[.!?]+|[^.!?\n]+$/g) || []
                const hitSentence = sentences.find((s) => containsMention(s))
                if (hitSentence) return hitSentence.trim()

                // d) Fallback: nur die Trefferzeile (muss Brand enthalten)
                return ensure(hitLine.trim()) || "-"
            },
            normalizeEntityToken(value) {
                return (value || "")
                    .toString()
                    .normalize("NFKD")
                    .replace(/[\u2018\u2019\u0060\u00B4]/g, "'")
                    .replace(/['’`´]/g, "")
                    .replace(/\s+/g, " ")
                    .trim()
                    .toLowerCase()
            },
            resolveCanonicalBrandLabel(entity) {
                if (!Array.isArray(this.brands) || !this.brands.length || !entity) return null

                const candidateValues = [entity?.canonicalName, entity?.name, entity?.brand_name]
                if (Array.isArray(entity?.mentions)) {
                    entity.mentions.forEach((m) => candidateValues.push(typeof m === "string" ? m : m?.text?.content || m?.content || ""))
                }

                const candidateSet = new Set(candidateValues.map((v) => this.normalizeEntityToken(v)).filter(Boolean))
                if (!candidateSet.size) return null

                for (const b of this.brands) {
                    const canonicalLabel = (b?.canonicalName || b?.brand_name || "").toString().trim()
                    if (!canonicalLabel) continue

                    const brandTokens = new Set()
                    const canonicalToken = this.normalizeEntityToken(canonicalLabel)
                    if (canonicalToken) brandTokens.add(canonicalToken)
                    ;(Array.isArray(b?.mentions) ? b.mentions : []).forEach((m) => {
                        const token = this.normalizeEntityToken(typeof m === "string" ? m : m?.text?.content || m?.content || "")
                        if (token) brandTokens.add(token)
                    })

                    for (const token of candidateSet) {
                        if (brandTokens.has(token)) return canonicalLabel
                    }
                }

                return null
            },
            entityDisplayName(entity) {
                return this.resolveCanonicalBrandLabel(entity) || entity?.canonicalName || entity?.name || entity?.brand_name || "Unknown"
            },
            getDisplayEntities(item) {
                const list = Array.isArray(item?.mergedEntities) ? item.mergedEntities : []
                const out = []
                const seen = new Set()

                list.forEach((entity, idx) => {
                    const label = this.entityDisplayName(entity)
                    const labelToken = this.normalizeEntityToken(label)
                    if (!labelToken || seen.has(labelToken)) return
                    seen.add(labelToken)
                    out.push({
                        entity,
                        label,
                        key: entity?._id || `${labelToken}-${idx}`
                    })
                })

                out.sort((a, b) => {
                    const aPrimary = this.isProjectBrandEntity(a) ? 1 : 0
                    const bPrimary = this.isProjectBrandEntity(b) ? 1 : 0
                    if (aPrimary !== bPrimary) return bPrimary - aPrimary
                    return a.label.localeCompare(b.label)
                })

                return out
            },
            getHiddenEntities(item) {
                const all = this.getDisplayEntities(item)
                return all.length > 4 ? all.slice(4) : []
            },
            getProjectBrandEntityTokenSet() {
                const set = new Set()
                const add = (value) => {
                    const token = this.normalizeEntityToken(value)
                    if (token) set.add(token)
                }

                add(this.project?.brand?.brand_name)
                add(this.project?.brand?.canonicalName)
                add(this.project?.brand?.name)
                ;(Array.isArray(this.project?.brand?.mentions) ? this.project.brand.mentions : []).forEach((m) => {
                    if (typeof m === "string") add(m)
                    else add(m?.text?.content || m?.content || m?.name)
                })

                return set
            },
            isProjectBrandEntity(entry) {
                const projectTokens = this.getProjectBrandEntityTokenSet()
                if (!projectTokens.size) return false

                const entity = entry?.entity || entry
                const candidates = [entry?.label, entity?.canonicalName, entity?.name, entity?.brand_name]
                ;(Array.isArray(entity?.mentions) ? entity.mentions : []).forEach((m) => {
                    candidates.push(typeof m === "string" ? m : m?.text?.content || m?.content || m?.name)
                })

                return candidates.some((value) => {
                    const token = this.normalizeEntityToken(value)
                    return token && projectTokens.has(token)
                })
            },
            entityDotColor(entity) {
                const key = this.normalizeEntityToken(this.entityDisplayName(entity))
                return getBrandColorFromName(key)
            },
            entityDotStyle(entity) {
                const color = this.entityDotColor(entity)
                return {
                    backgroundColor: color
                }
            },
            entityChipStyle(entity) {
                const color = this.entityDotColor(entity)
                return {
                    borderColor: `${color}66`,
                    backgroundColor: `${color}10`
                }
            },
            renderMentionMarkdown(item) {
                let snippet = this.getMentionSnippet(item)
                if (!snippet || snippet === "-") return "-"

                // Vorhandene Bold-Markups entfernen
                snippet = snippet.replace(/\*\*(.*?)\*\*/g, "$1")

                const urlPlaceholders = []
                const replaceUrl = (url) => {
                    const key = `__URL_PLACEHOLDER_${urlPlaceholders.length}__`
                    urlPlaceholders.push({ key, url })
                    return key
                }

                const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g
                snippet = snippet.replace(linkRegex, (match, text, url) => `[${text}](${replaceUrl(url)})`)

                const bareUrlRegex = /(https?:\/\/[^\s)]+|www\.[^\s)]+)/gi
                snippet = snippet.replace(bareUrlRegex, (url) => replaceUrl(url))

                // Brand-Mentions + ggf. Match-Name fett (case-insensitive)
                const extra = []
                if (item.mergedEntities && Array.isArray(item.mergedEntities)) {
                    const match = item.mergedEntities.find((e) => e.name && (this.brand.mentions || []).some((m) => (typeof m === "string" ? m.toLowerCase() === e.name.toLowerCase() : false)))
                    if (match && match.name && !extra.includes(match.name)) extra.push(match.name)
                }
                const rawTerms = [...(Array.isArray(this.brand.mentions) ? this.brand.mentions : []), ...extra]
                const terms = Array.from(
                    new Set(
                        rawTerms
                            .map((mention) => (typeof mention === "string" ? mention : mention?.text?.content || mention?.content || ""))
                            .map((s) => (s || "").trim())
                            .filter(Boolean)
                    )
                ).sort((a, b) => b.length - a.length)

                if (terms.length) {
                    const alternation = terms.map((t) => this.escapeRegExp(t)).join("|")
                    const regex = new RegExp(`\\b(${alternation})\\b`, "gi")
                    snippet = snippet.replace(regex, "**$1**")
                }

                urlPlaceholders.forEach(({ key, url }) => {
                    snippet = snippet.replace(new RegExp(this.escapeRegExp(key), "g"), url)
                })

                return this.md.render(snippet)
            },
            getPlatformInfo(name) {
                // Name normalisieren (z.B. für "openai" → "openai")
                const key = (name || "").toString().trim().toLowerCase()
                const platforms = {
                    openai: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.639"><path fill="#fff" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.612C52.026 509.64 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill-rule="nonzero" d="M412.037 221.764a90.834 90.834 0 004.648-28.67 90.79 90.79 0 00-12.443-45.87c-16.37-28.496-46.738-46.089-79.605-46.089-6.466 0-12.943.683-19.264 2.04a90.765 90.765 0 00-67.881-30.515h-.576c-.059.002-.149.002-.216.002-39.807 0-75.108 25.686-87.346 63.554-25.626 5.239-47.748 21.31-60.682 44.03a91.873 91.873 0 00-12.407 46.077 91.833 91.833 0 0023.694 61.553 90.802 90.802 0 00-4.649 28.67 90.804 90.804 0 0012.442 45.87c16.369 28.504 46.74 46.087 79.61 46.087a91.81 91.81 0 0019.253-2.04 90.783 90.783 0 0067.887 30.516h.576l.234-.001c39.829 0 75.119-25.686 87.357-63.588 25.626-5.242 47.748-21.312 60.682-44.033a91.718 91.718 0 0012.383-46.035 91.83 91.83 0 00-23.693-61.553l-.004-.005zM275.102 413.161h-.094a68.146 68.146 0 01-43.611-15.8 56.936 56.936 0 002.155-1.221l72.54-41.901a11.799 11.799 0 005.962-10.251V241.651l30.661 17.704c.326.163.55.479.596.84v84.693c-.042 37.653-30.554 68.198-68.21 68.273h.001zm-146.689-62.649a68.128 68.128 0 01-9.152-34.085c0-3.904.341-7.817 1.005-11.663.539.323 1.48.897 2.155 1.285l72.54 41.901a11.832 11.832 0 0011.918-.002l88.563-51.137v35.408a1.1 1.1 0 01-.438.94l-73.33 42.339a68.43 68.43 0 01-34.11 9.12 68.359 68.359 0 01-59.15-34.11l-.001.004zm-19.083-158.36a68.044 68.044 0 0135.538-29.934c0 .625-.036 1.731-.036 2.5v83.801l-.001.07a11.79 11.79 0 005.954 10.242l88.564 51.13-30.661 17.704a1.096 1.096 0 01-1.034.093l-73.337-42.375a68.36 68.36 0 01-34.095-59.143 68.412 68.412 0 019.112-34.085l-.004-.003zm251.907 58.621l-88.563-51.137 30.661-17.697a1.097 1.097 0 011.034-.094l73.337 42.339c21.109 12.195 34.132 34.746 34.132 59.132 0 28.604-17.849 54.199-44.686 64.078v-86.308c.004-.032.004-.065.004-.096 0-4.219-2.261-8.119-5.919-10.217zm30.518-45.93c-.539-.331-1.48-.898-2.155-1.286l-72.54-41.901a11.842 11.842 0 00-5.958-1.611c-2.092 0-4.15.558-5.957 1.611l-88.564 51.137v-35.408l-.001-.061a1.1 1.1 0 01.44-.88l73.33-42.303a68.301 68.301 0 0134.108-9.129c37.704 0 68.281 30.577 68.281 68.281a68.69 68.69 0 01-.984 11.545v.005zm-191.843 63.109l-30.668-17.704a1.09 1.09 0 01-.596-.84v-84.692c.016-37.685 30.593-68.236 68.281-68.236a68.332 68.332 0 0143.689 15.804 63.09 63.09 0 00-2.155 1.222l-72.54 41.9a11.794 11.794 0 00-5.961 10.248v.068l-.05 102.23zm16.655-35.91l39.445-22.782 39.444 22.767v45.55l-39.444 22.767-39.445-22.767v-45.535z"/></svg>`,
                        label: "openai",
                        status: this.platforms.includes("openai") ? "active" : "upgrade"
                    },
                    chatgpt: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.639"><path fill="#fff" d="M115.612 0h280.775C459.974 0 512 52.026 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.612C52.026 509.64 0 457.614 0 394.027V115.612C0 52.026 52.026 0 115.612 0z"/><path fill-rule="nonzero" d="M412.037 221.764a90.834 90.834 0 004.648-28.67 90.79 90.79 0 00-12.443-45.87c-16.37-28.496-46.738-46.089-79.605-46.089-6.466 0-12.943.683-19.264 2.04a90.765 90.765 0 00-67.881-30.515h-.576c-.059.002-.149.002-.216.002-39.807 0-75.108 25.686-87.346 63.554-25.626 5.239-47.748 21.31-60.682 44.03a91.873 91.873 0 00-12.407 46.077 91.833 91.833 0 0023.694 61.553 90.802 90.802 0 00-4.649 28.67 90.804 90.804 0 0012.442 45.87c16.369 28.504 46.74 46.087 79.61 46.087a91.81 91.81 0 0019.253-2.04 90.783 90.783 0 0067.887 30.516h.576l.234-.001c39.829 0 75.119-25.686 87.357-63.588 25.626-5.242 47.748-21.312 60.682-44.033a91.718 91.718 0 0012.383-46.035 91.83 91.83 0 00-23.693-61.553l-.004-.005zM275.102 413.161h-.094a68.146 68.146 0 01-43.611-15.8 56.936 56.936 0 002.155-1.221l72.54-41.901a11.799 11.799 0 005.962-10.251V241.651l30.661 17.704c.326.163.55.479.596.84v84.693c-.042 37.653-30.554 68.198-68.21 68.273h.001zm-146.689-62.649a68.128 68.128 0 01-9.152-34.085c0-3.904.341-7.817 1.005-11.663.539.323 1.48.897 2.155 1.285l72.54 41.901a11.832 11.832 0 0011.918-.002l88.563-51.137v35.408a1.1 1.1 0 01-.438.94l-73.33 42.339a68.43 68.43 0 01-34.11 9.12 68.359 68.359 0 01-59.15-34.11l-.001.004zm-19.083-158.36a68.044 68.044 0 0135.538-29.934c0 .625-.036 1.731-.036 2.5v83.801l-.001.07a11.79 11.79 0 005.954 10.242l88.564 51.13-30.661 17.704a1.096 1.096 0 01-1.034.093l-73.337-42.375a68.36 68.36 0 01-34.095-59.143 68.412 68.412 0 019.112-34.085l-.004-.003zm251.907 58.621l-88.563-51.137 30.661-17.697a1.097 1.097 0 011.034-.094l73.337 42.339c21.109 12.195 34.132 34.746 34.132 59.132 0 28.604-17.849 54.199-44.686 64.078v-86.308c.004-.032.004-.065.004-.096 0-4.219-2.261-8.119-5.919-10.217zm30.518-45.93c-.539-.331-1.48-.898-2.155-1.286l-72.54-41.901a11.842 11.842 0 00-5.958-1.611c-2.092 0-4.15.558-5.957 1.611l-88.564 51.137v-35.408l-.001-.061a1.1 1.1 0 01.44-.88l73.33-42.303a68.301 68.301 0 0134.108-9.129c37.704 0 68.281 30.577 68.281 68.281a68.69 68.69 0 01-.984 11.545v.005zm-191.843 63.109l-30.668-17.704a1.09 1.09 0 01-.596-.84v-84.692c.016-37.685 30.593-68.236 68.281-68.236a68.332 68.332 0 0143.689 15.804 63.09 63.09 0 00-2.155 1.222l-72.54 41.9a11.794 11.794 0 00-5.961 10.248v.068l-.05 102.23zm16.655-35.91l39.445-22.782 39.444 22.767v45.55l-39.444 22.767-39.445-22.767v-45.535z"/></svg>`,
                        label: "ChatGPT",
                        status: this.platforms.includes("chatgpt") ? "active" : "upgrade"
                    },
                    google: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path></svg>`,
                        label: "Google AIO",
                        status: this.google ? "active" : "upgrade"
                    },
                    gemini: {
                        logo: `<svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 65 65"><mask id="maskme" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="65" height="65"><path d="M32.447 0c.68 0 1.273.465 1.439 1.125a38.904 38.904 0 001.999 5.905c2.152 5 5.105 9.376 8.854 13.125 3.751 3.75 8.126 6.703 13.125 8.855a38.98 38.98 0 005.906 1.999c.66.166 1.124.758 1.124 1.438 0 .68-.464 1.273-1.125 1.439a38.902 38.902 0 00-5.905 1.999c-5 2.152-9.375 5.105-13.125 8.854-3.749 3.751-6.702 8.126-8.854 13.125a38.973 38.973 0 00-2 5.906 1.485 1.485 0 01-1.438 1.124c-.68 0-1.272-.464-1.438-1.125a38.913 38.913 0 00-2-5.905c-2.151-5-5.103-9.375-8.854-13.125-3.75-3.749-8.125-6.702-13.125-8.854a38.973 38.973 0 00-5.905-2A1.485 1.485 0 010 32.448c0-.68.465-1.272 1.125-1.438a38.903 38.903 0 005.905-2c5-2.151 9.376-5.104 13.125-8.854 3.75-3.749 6.703-8.125 8.855-13.125a38.972 38.972 0 001.999-5.905A1.485 1.485 0 0132.447 0z" fill="#000"/><path d="M32.447 0c.68 0 1.273.465 1.439 1.125a38.904 38.904 0 001.999 5.905c2.152 5 5.105 9.376 8.854 13.125 3.751 3.75 8.126 6.703 13.125 8.855a38.98 38.98 0 005.906 1.999c.66.166 1.124.758 1.124 1.438 0 .68-.464 1.273-1.125 1.439a38.902 38.902 0 00-5.905 1.999c-5 2.152-9.375 5.105-13.125 8.854-3.749 3.751-6.702 8.126-8.854 13.125a38.973 38.973 0 00-2 5.906 1.485 1.485 0 01-1.438 1.124c-.68 0-1.272-.464-1.438-1.125a38.913 38.913 0 00-2-5.905c-2.151-5-5.103-9.375-8.854-13.125-3.75-3.749-8.125-6.702-13.125-8.854a38.973 38.973 0 00-5.905-2A1.485 1.485 0 010 32.448c0-.68.465-1.272 1.125-1.438a38.903 38.903 0 005.905-2c5-2.151 9.376-5.104 13.125-8.854 3.75-3.749 6.703-8.125 8.855-13.125a38.972 38.972 0 001.999-5.905A1.485 1.485 0 0132.447 0z" fill="url(#prefix__paint0_linear_2001_67)"/></mask><g mask="url(#maskme)"><g filter="url(#prefix__filter0_f_2001_67)"><path d="M-5.859 50.734c7.498 2.663 16.116-2.33 19.249-11.152 3.133-8.821-.406-18.131-7.904-20.794-7.498-2.663-16.116 2.33-19.25 11.151-3.132 8.822.407 18.132 7.905 20.795z" fill="#FFE432"/></g><g filter="url(#prefix__filter1_f_2001_67)"><path d="M27.433 21.649c10.3 0 18.651-8.535 18.651-19.062 0-10.528-8.35-19.062-18.651-19.062S8.78-7.94 8.78 2.587c0 10.527 8.35 19.062 18.652 19.062z" fill="#FC413D"/></g><g filter="url(#prefix__filter2_f_2001_67)"><path d="M20.184 82.608c10.753-.525 18.918-12.244 18.237-26.174-.68-13.93-9.95-24.797-20.703-24.271C6.965 32.689-1.2 44.407-.519 58.337c.681 13.93 9.95 24.797 20.703 24.271z" fill="#00B95C"/></g><g filter="url(#prefix__filter3_f_2001_67)"><path d="M20.184 82.608c10.753-.525 18.918-12.244 18.237-26.174-.68-13.93-9.95-24.797-20.703-24.271C6.965 32.689-1.2 44.407-.519 58.337c.681 13.93 9.95 24.797 20.703 24.271z" fill="#00B95C"/></g><g filter="url(#prefix__filter4_f_2001_67)"><path d="M30.954 74.181c9.014-5.485 11.427-17.976 5.389-27.9-6.038-9.925-18.241-13.524-27.256-8.04-9.015 5.486-11.428 17.977-5.39 27.902 6.04 9.924 18.242 13.523 27.257 8.038z" fill="#00B95C"/></g><g filter="url(#prefix__filter5_f_2001_67)"><path d="M67.391 42.993c10.132 0 18.346-7.91 18.346-17.666 0-9.757-8.214-17.667-18.346-17.667s-18.346 7.91-18.346 17.667c0 9.757 8.214 17.666 18.346 17.666z" fill="#3186FF"/></g><g filter="url(#prefix__filter6_f_2001_67)"><path d="M-13.065 40.944c9.33 7.094 22.959 4.869 30.442-4.972 7.483-9.84 5.987-23.569-3.343-30.663C4.704-1.786-8.924.439-16.408 10.28c-7.483 9.84-5.986 23.57 3.343 30.664z" fill="#FBBC04"/></g><g filter="url(#prefix__filter7_f_2001_67)"><path d="M34.74 51.43c11.135 7.656 25.896 5.524 32.968-4.764 7.073-10.287 3.779-24.832-7.357-32.488C49.215 6.52 34.455 8.654 27.382 18.94c-7.072 10.288-3.779 24.833 7.357 32.49z" fill="#3186FF"/></g><g filter="url(#prefix__filter8_f_2001_67)"><path d="M54.984-2.336c2.833 3.852-.808 11.34-8.131 16.727-7.324 5.387-15.557 6.631-18.39 2.78-2.833-3.853.807-11.342 8.13-16.728 7.324-5.387 15.558-6.631 18.39-2.78z" fill="#749BFF"/></g><g filter="url(#prefix__filter9_f_2001_67)"><path d="M31.727 16.104C43.053 5.598 46.94-8.626 40.41-15.666c-6.53-7.04-21.006-4.232-32.332 6.274s-15.214 24.73-8.683 31.77c6.53 7.04 21.006 4.232 32.332-6.274z" fill="#FC413D"/></g><g filter="url(#prefix__filter10_f_2001_67)"><path d="M8.51 53.838c6.732 4.818 14.46 5.55 17.262 1.636 2.802-3.915-.384-10.994-7.116-15.812-6.731-4.818-14.46-5.55-17.261-1.636-2.802 3.915.383 10.994 7.115 15.812z" fill="#FFEE48"/></g></g><defs><filter id="prefix__filter0_f_2001_67" x="-19.824" y="13.152" width="39.274" height="43.217" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="2.46" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter1_f_2001_67" x="-15.001" y="-40.257" width="84.868" height="85.688" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="11.891" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter2_f_2001_67" x="-20.776" y="11.927" width="79.454" height="90.916" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter3_f_2001_67" x="-20.776" y="11.927" width="79.454" height="90.916" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter4_f_2001_67" x="-19.845" y="15.459" width="79.731" height="81.505" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="10.109" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter5_f_2001_67" x="29.832" y="-11.552" width="75.117" height="73.758" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="9.606" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter6_f_2001_67" x="-38.583" y="-16.253" width="78.135" height="78.758" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="8.706" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter7_f_2001_67" x="8.107" y="-5.966" width="78.877" height="77.539" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="7.775" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter8_f_2001_67" x="13.587" y="-18.488" width="56.272" height="51.81" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="6.957" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter9_f_2001_67" x="-15.526" y="-31.297" width="70.856" height="69.306" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="5.876" result="effect1_foregroundBlur_2001_67"/></filter><filter id="prefix__filter10_f_2001_67" x="-14.168" y="20.964" width="55.501" height="51.571" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape"/><feGaussianBlur stdDeviation="7.273" result="effect1_foregroundBlur_2001_67"/></filter><linearGradient id="prefix__paint0_linear_2001_67" x1="18.447" y1="43.42" x2="52.153" y2="15.004" gradientUnits="userSpaceOnUse"><stop stop-color="#4893FC"/><stop offset=".27" stop-color="#4893FC"/><stop offset=".777" stop-color="#969DFF"/><stop offset="1" stop-color="#BD99FE"/></linearGradient></defs></svg>`,
                        label: "Gemini",
                        status: this.platforms.includes("gemini") ? "active" : "upgrade"
                    },
                    perplexity: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd" viewBox="0 0 512 509.64"><path fill="#1F1F1F" d="M115.613 0h280.774C459.974 0 512 52.025 512 115.612v278.415c0 63.587-52.026 115.613-115.613 115.613H115.613C52.026 509.64 0 457.614 0 394.027V115.612C0 52.025 52.026 0 115.613 0z"/><path fill="#fff" fill-rule="nonzero" d="M348.851 128.063l-68.946 58.302h68.946v-58.302zm-83.908 48.709l100.931-85.349v94.942h32.244v143.421h-38.731v90.004l-94.442-86.662v83.946h-17.023v-83.906l-96.596 86.246v-89.628h-37.445V186.365h38.732V90.768l95.309 84.958v-83.16h17.023l-.002 84.206zm-29.209 26.616c-34.955.02-69.893 0-104.83 0v109.375h20.415v-27.121l84.415-82.254zm41.445 0l82.208 82.324v27.051h21.708V203.388c-34.617 0-69.274.02-103.916 0zm-42.874-17.023l-64.669-57.646v57.646h64.669zm13.617 124.076v-95.2l-79.573 77.516v88.731l79.573-71.047zm17.252-95.022v94.863l77.19 70.83c0-29.485-.012-58.943-.012-88.425l-77.178-77.268z"/></svg>`,
                        label: "Perplexity",
                        status: this.project.platforms.includes("perplexity") ? "active" : "upgrade"
                    },
                    grok: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" ><path d="M18.542 30.532l15.956-11.776c.783-.576 1.902-.354 2.274.545 1.962 4.728 1.084 10.411-2.819 14.315-3.903 3.901-9.333 4.756-14.299 2.808l-5.423 2.511c7.778 5.315 17.224 4 23.125-1.903 4.682-4.679 6.131-11.058 4.775-16.812l.011.011c-1.966-8.452.482-11.829 5.501-18.735C47.759 1.332 47.88 1.166 48 1l-6.602 6.599V7.577l-22.86 22.958M15.248 33.392c-5.582-5.329-4.619-13.579.142-18.339 3.521-3.522 9.294-4.958 14.331-2.847l5.412-2.497c-.974-.704-2.224-1.46-3.659-1.994-6.478-2.666-14.238-1.34-19.505 3.922C6.904 16.701 5.31 24.488 8.045 31.133c2.044 4.965-1.307 8.48-4.682 12.023C2.164 44.411.967 45.67 0 47l15.241-13.608"/></svg>`,
                        label: "Grok",
                        status: this.project.platforms.includes("grok") ? "active" : "upgrade"
                    },
                    google_ai_mode: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"></path><path fill="#FF3D00" d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"></path><path fill="#4CAF50" d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"></path><path fill="#1976D2" d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"></path></svg>`,
                        label: "Google AI",
                        status: this.project.platforms.includes("google_ai_mode") ? "active" : "upgrade"
                    },
                    microsoft_copilot: {
                        logo: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 48 48"><path d="M34.142 7.325A4.63 4.63 0 0029.7 4H28.35a4.63 4.63 0 00-4.554 3.794L21.48 20.407l.575-1.965a4.63 4.63 0 014.444-3.33h7.853l3.294 1.282 3.175-1.283h-.926a4.63 4.63 0 01-4.443-3.325l-1.31-4.461z" fill="url(#prefix__paint0_radial_56201_15503)"/><path d="M14.33 40.656A4.63 4.63 0 0018.779 44h2.87a4.63 4.63 0 004.629-4.51l.312-12.163-.654 2.233a4.63 4.63 0 01-4.443 3.329h-7.919l-2.823-1.532-3.057 1.532h.912a4.63 4.63 0 014.447 3.344l1.279 4.423z" fill="url(#prefix__paint1_radial_56201_15503)"/><path d="M29.5 4H13.46c-4.583 0-7.332 6.057-9.165 12.113C2.123 23.29-.72 32.885 7.503 32.885h6.925a4.63 4.63 0 004.456-3.358 2078.617 2078.617 0 014.971-17.156c.843-2.843 1.544-5.284 2.621-6.805C27.08 4.714 28.086 4 29.5 4z" fill="url(#prefix__paint2_linear_56201_15503)"/><path d="M29.5 4H13.46c-4.583 0-7.332 6.057-9.165 12.113C2.123 23.29-.72 32.885 7.503 32.885h6.925a4.63 4.63 0 004.456-3.358 2078.617 2078.617 0 014.971-17.156c.843-2.843 1.544-5.284 2.621-6.805C27.08 4.714 28.086 4 29.5 4z" fill="url(#prefix__paint3_linear_56201_15503)"/><path d="M18.498 44h16.04c4.582 0 7.332-6.058 9.165-12.115 2.171-7.177 5.013-16.775-3.208-16.775h-6.926a4.63 4.63 0 00-4.455 3.358 2084.036 2084.036 0 01-4.972 17.16c-.842 2.843-1.544 5.285-2.62 6.806-.604.852-1.61 1.566-3.024 1.566z" fill="url(#prefix__paint4_radial_56201_15503)"/><path d="M18.498 44h16.04c4.582 0 7.332-6.058 9.165-12.115 2.171-7.177 5.013-16.775-3.208-16.775h-6.926a4.63 4.63 0 00-4.455 3.358 2084.036 2084.036 0 01-4.972 17.16c-.842 2.843-1.544 5.285-2.62 6.806-.604.852-1.61 1.566-3.024 1.566z" fill="url(#prefix__paint5_linear_56201_15503)"/><defs><radialGradient id="prefix__paint0_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="matrix(-10.96051 -13.38922 12.59013 -10.30637 38.005 20.514)"><stop offset=".096" stop-color="#00AEFF"/><stop offset=".773" stop-color="#2253CE"/><stop offset="1" stop-color="#0736C4"/></radialGradient><radialGradient id="prefix__paint1_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="rotate(51.84 -28.201 27.85) scale(15.9912 15.5119)"><stop stop-color="#FFB657"/><stop offset=".634" stop-color="#FF5F3D"/><stop offset=".923" stop-color="#C02B3C"/></radialGradient><radialGradient id="prefix__paint4_radial_56201_15503" cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="rotate(109.274 16.301 20.802) scale(38.3873 45.9867)"><stop offset=".066" stop-color="#8C48FF"/><stop offset=".5" stop-color="#F2598A"/><stop offset=".896" stop-color="#FFB152"/></radialGradient><linearGradient id="prefix__paint2_linear_56201_15503" x1="12.5" y1="7.5" x2="14.788" y2="33.975" gradientUnits="userSpaceOnUse"><stop offset=".156" stop-color="#0D91E1"/><stop offset=".487" stop-color="#52B471"/><stop offset=".652" stop-color="#98BD42"/><stop offset=".937" stop-color="#FFC800"/></linearGradient><linearGradient id="prefix__paint3_linear_56201_15503" x1="14.5" y1="4" x2="15.75" y2="32.885" gradientUnits="userSpaceOnUse"><stop stop-color="#3DCBFF"/><stop offset=".247" stop-color="#0588F7" stop-opacity="0"/></linearGradient><linearGradient id="prefix__paint5_linear_56201_15503" x1="42.586" y1="13.346" x2="42.569" y2="21.215" gradientUnits="userSpaceOnUse"><stop offset=".058" stop-color="#F8ADFA"/><stop offset=".708" stop-color="#A86EDD" stop-opacity="0"/></linearGradient></defs></svg>`,
                        label: "Microsoft Copilot",
                        status: this.project.platforms.includes("microsoft_copilot") ? "active" : "upgrade"
                    },
                    amazon_rufus: {
                        logo: AMAZON_RUFUS_LOGO,
                        label: "Amazon Rufus",
                        status: this.project.platforms.includes("amazon_rufus") ? "active" : "upgrade"
                    }
                }
                return platforms[key] || { logo: null, label: name, status: null }
            },
            escapeRegExp(string) {
                // Hilfsfunktion für Regex-Escaping
                return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
            },
            async onBrandAvatarClick(entity) {
                // Brand anhand der Entität suchen
                const brand = this.brands.find((b) => (b.mentions || []).some((m) => m.toLowerCase() === entity.name.toLowerCase()))
                if (brand) {
                    // Brand als ausgewählt setzen
                    // Falls du ein v-model oder sync verwendest, emitte das Event:
                    // this.$emit("update:brand", brand)
                    // Oder direkt im State setzen:
                    this.$set(this, "brand", brand)
                    // Seite zurücksetzen
                    this.options.page = 1
                    // Neu laden
                    await this.fetchData()
                }
            },
            exportTableData() {
                const headers = this.headers.map((h) => h.text)
                const rows = this.promptResponses.map((item) =>
                    this.headers.map((h) => {
                        // Speziell für Mention: Nutze den Klartext-Snippet ohne Markdown
                        if (h.value === "mention") {
                            let snippet = this.getMentionSnippet(item)
                            // Entferne Markdown (fett, etc.)
                            snippet = snippet.replace(/\*\*(.*?)\*\*/g, "$1")
                            // Entferne evtl. weitere Markdown-Sonderzeichen
                            snippet = snippet.replace(/[_*`~]/g, "")
                            return `"${snippet || ""}"`
                        }
                        // Speziell für Brands: Zeige alle canonicalNames aus mergedEntities kommasepariert
                        if (h.value === "brands") {
                            if (Array.isArray(item.mergedEntities)) {
                                return `"${item.mergedEntities.map((e) => e.canonicalName || e.name || "").join(", ")}"`
                            }
                            return ""
                        }
                        if (h.value === "sentiment") {
                            const score = this.getSentimentScore(item)
                            return score === null ? "-" : `${(score * 100).toFixed(0)}%`
                        }
                        // NEU: Sources-Export (Domains, Fallback URL)
                        if (h.value === "sources") {
                            const list = Array.isArray(item.sources) ? item.sources : []
                            const vals = list.map((s) => s.domain || this.domainFromUrl(s.url || s.key || "") || s.url || s.key || "").filter(Boolean)
                            return `"${vals.join(", ").replace(/"/g, '""')}"`
                        }

                        // Standardbehandlung
                        let val = item[h.value]
                        if (Array.isArray(val)) return `"${val.join(", ")}"`
                        if (typeof val === "object" && val !== null) return `"${JSON.stringify(val)}"`
                        if (typeof val === "string") return `"${val.replace(/"/g, '""')}"`
                        return val !== undefined && val !== null ? val : ""
                    })
                )
                return [headers, ...rows].map((e) => e.join(";")).join("\n")
            }
        },
        mounted() {
            if (this.isActive) this.fetchData()
        }
    }
</script>
<style>
    #mentionTable tbody tr {
        cursor: pointer;
    }
    #mentionTable th {
        white-space: nowrap !important;
        text-transform: uppercase !important;
        font-weight: 300 !important;
    }
    .entity-chip-list {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        max-width: 520px;
        gap: 2px;
    }
    .entity-chip {
        border-radius: 999px !important;
        font-weight: 600;
        letter-spacing: 0.1px;
        transition: all 0.16s ease;
    }
    .entity-chip:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
    }
    .entity-chip-dot {
        width: 10px;
        height: 10px;
        border-radius: 999px;
        margin-right: 8px;
        flex: 0 0 auto;
        box-shadow: 0 0 0 2px #fff, 0 0 0 3px rgba(0, 0, 0, 0.08);
        position: relative;
        top: 1px;
    }
    .theme--dark .entity-chip-dot {
        box-shadow: 0 0 0 2px #1f1f1f, 0 0 0 3px rgba(255, 255, 255, 0.08);
    }
    .entity-chip-label {
        max-width: 180px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: inline-block;
    }
    .entity-chip-more {
        border-radius: 999px !important;
    }
    .entity-more-tooltip {
        min-width: 180px;
        max-width: 320px;
    }
    .entity-more-tooltip-row + .entity-more-tooltip-row {
        margin-top: 4px;
    }
    .entity-more-tooltip-label--primary {
        font-weight: 700;
    }
    .mention-sentiment-chip {
        display: inline-flex;
        align-items: center;
        min-width: 78px;
    }
    .v-data-table .v-data-table__wrapper td {
        padding-top: 12px !important;
        padding-bottom: 12px !important;
    }
    #mentionTable th.col-brands,
    #mentionTable td.col-brands {
        min-width: 320px;
    }
    #mentionTable strong {
        color: inherit;
        border: none;
        border-radius: 0;
        padding: 0;
        background: transparent;
        font-weight: 600;
    }
</style>
